import { Color, ITheme, SizeUnit } from 'tuui/lib/theme';
import { getScaledSize, theme } from 'tuui/lib/utils/styles';
import { getCSSVar } from 'tuui/lib/utils/cssVariables';

export type TagVariant = 'filled' | 'outlined';
export type TagSize = keyof Pick<ITheme['sizes']['scales'], 'md' | 'sm'>;
export type TagColor = keyof Pick<ITheme['palette'], 'primary' | 'secondary' | 'warning' | 'error'>;
export type Pixel = '0' | `${number}px`;

type ThemeColorProducer = ({ theme, $color }: Pick<TagStyledProps, 'theme' | '$color'>) => Color;
type ThemePaddingProducer = ({ theme, $size }: Pick<TagStyledProps, 'theme' | '$size'>) => `${SizeUnit} ${SizeUnit}`;
type ThemeFontSizeProducer = ({ theme, $size }: Pick<TagStyledProps, 'theme' | '$size'>) => SizeUnit;


export type TagStyledProps = {
    theme: ITheme;
    $variant: TagVariant;
    $color: TagColor;
    $size: TagSize;
    $disabled?: boolean;
    $clickable?: boolean;
};

export interface TagVariantSpec {
    fill: ThemeColorProducer;
    text: ThemeColorProducer;
    padding: ThemePaddingProducer;
    fontSize: ThemeFontSizeProducer;
    stroke: ThemeColorProducer;
    disabled?: Partial<Pick<TagVariantSpec, TagVariantValue>>;
    active?: Partial<Pick<TagVariantSpec, TagVariantValue>>;
}

type TagVariantValue = keyof Pick<TagVariantSpec, 'fill' | 'text' | 'padding' | 'fontSize' | 'stroke'>;
type TagVariantState = keyof Omit<TagVariantSpec, TagVariantValue>;

const getTagPadding: ThemePaddingProducer = ({ theme, $size }) =>
    $size === 'md'
        ? `${getScaledSize(theme, 'sm')} ${getScaledSize(theme, 'md')}`
        : `${getScaledSize(theme, 'xs')} ${getScaledSize(theme, 'sm')}`;

const getTagFontSize: ThemeFontSizeProducer = ({ theme, $size }) =>
    $size === 'md' ? theme.typography.sizes.lg : theme.typography.sizes.md;

const tagDisabledState: Pick<TagVariantSpec, 'fill' | 'text' | 'stroke'> = {
    fill: theme('palette.disabled.fill'),
    text: theme('palette.grayscale.5'),
    stroke: theme('palette.grayscale.5'),
};

// TODO: Color prop support
// The TagColor prop (primary, secondary, warning, error) is available in the component API
// but not yet implemented in the styling functions below.

const filled: TagVariantSpec = {
    text: () => getCSSVar('content.secondary.base') as Color,
    fill: () => getCSSVar('background.primary.base') as Color,
    stroke: () => getCSSVar('background.primary.base') as Color, // No visible border for filled
    padding: getTagPadding,
    fontSize: getTagFontSize,
    disabled: tagDisabledState,
    active: {
        fill: () => getCSSVar('background.secondary.base') as Color,
        text: () => getCSSVar('content.secondary.base') as Color,
    },
};

const outlined: TagVariantSpec = {
    text: () => getCSSVar('content.secondary.base') as Color,
    fill: () => getCSSVar('background.primary.base') as Color,
    stroke: () => getCSSVar('border.primary') as Color,
    padding: getTagPadding,
    fontSize: getTagFontSize,
    disabled: tagDisabledState,
    active: {
        fill: () => getCSSVar('background.secondary.base') as Color,
        text: () => getCSSVar('content.secondary.base') as Color,
        stroke: () => getCSSVar('border.primary') as Color,
    },
};

export const tagVariants: Record<TagVariant, TagVariantSpec> = {
    filled,
    outlined,
};

export const tagVariables = (
    state: TagVariantState,
    varName: TagVariantValue,
    { theme, $color, $variant, $size }: TagStyledProps
) => {
    const variantSpec = tagVariants[$variant];
    const varValueForState = variantSpec[state]?.[varName]?.({ theme, $color, $size });
    if (!varValueForState) {
        return null;
    }
    return `--${varName}: ${varValueForState};`;
};

export const applyDisabledState = () => ({ $disabled, ...tagBaseProps }: TagStyledProps) => {
    if (!$disabled) {
        return '';
    }

    return `    
    ${tagVariables('disabled', 'fill', tagBaseProps)};
    ${tagVariables('disabled', 'text', tagBaseProps)};
    ${tagVariables('disabled', 'stroke', tagBaseProps)};

    button {
        svg {
            color: ${tagBaseProps.theme.palette.disabled.text}
        }
    }`;
};

export const applyEnableState = () => ({ $variant, $clickable, $disabled, ...tagBaseProps }: TagStyledProps) => {
    if ($disabled || $clickable) {
        return '';
    }

    return `button {
        --text: ${tagVariants[$variant].text(tagBaseProps)};
        --fill: ${tagVariants[$variant].fill(tagBaseProps)};

        svg {
            color: var(--text);
        }

        &:hover:enabled {
            background-color: inherit;
            box-shadow: none;
            --text: ${tagVariants[$variant].text(tagBaseProps)};
            color: var(--text);
            
            svg {
                color: var(--text);
            }
        }

        &:active {
            background-color: var(--fill);
            
            svg {
                color: var(--text);
            }
        }

        &:disabled {
            box-shadow: none;
        }
    }`;
};

export const applyActiveState = () => ({ $disabled, $clickable, ...tagBaseProps }: TagStyledProps) => {
    if ($disabled || !$clickable) {
        return '';
    }
    return `
    &:active {
        ${tagVariables('active', 'stroke', tagBaseProps)};
        ${tagVariables('active', 'fill', tagBaseProps)};
        ${tagVariables('active', 'text', tagBaseProps)};
        background-color: var(--fill);
        color: var(--text);
        border-color: var(--stroke);

        button {
            ${tagVariables('active', 'fill', tagBaseProps)};
            background-color: var(--fill);

            svg {
                color: var(--text);
            }
        }
    }
    ${applyEnableState()({ $disabled, ...tagBaseProps })};
    `;
};
